// src/data/verbProgressStorage.js
import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";

/**
 * Key-Helper für Verb-Progress im LocalStorage
 */
function getVerbProgressKey(languageId, profileId, weekIndex) {
  const lang = languageId || "generic";
  const profile = profileId || "default";
  const w = weekIndex || 1;
  return `fluent_verb_progress_${profile}_${lang}_w${w}`;
}

// Frühere, spanisch benannte Personen-Schlüssel → sprachneutrale Schlüssel (siehe languages.js)
const LEGACY_FORM_KEYS = {
  yo: "1s",
  tu: "2s",
  el: "3s",
  nosotros: "1p",
  vosotros: "2p",
  ellos: "3p",
};

/** Stellt gespeicherte Form-Statistiken auf die neuen Schlüssel um, ohne Fortschritt zu verlieren */
export function migrateFormKeys(forms) {
  if (!forms || typeof forms !== "object") return forms;
  const result = {};
  for (const [key, value] of Object.entries(forms)) {
    result[LEGACY_FORM_KEYS[key] || key] = value;
  }
  return result;
}

function migrateVerbStates(states) {
  const result = {};
  for (const [id, state] of Object.entries(states || {})) {
    result[id] = state ? { ...state, forms: migrateFormKeys(state.forms) } : state;
  }
  return result;
}

/**
 * Lädt den Verb-Progress (als Objekt mit States pro Verb-ID).
 *
 * Online (User eingeloggt):
 *   - liest aus Supabase-Tabelle verb_progress
 *   - spiegelt den Stand in localStorage
 *
 * Offline / nicht eingeloggt:
 *   - liest nur aus localStorage (wie früher)
 */
export async function loadVerbProgress(languageId, profileId, weekIndex) {
  // Server-Side-Render-Schutz
  if (typeof window === "undefined") {
    return {};
  }

  // 1) Versuchen, User zu holen
  let userId = null;
  try {
    const { data, error } = await getSessionUser();
    if (!error && data?.user?.id) {
      userId = data.user.id;
    }
  } catch (e) {
    console.warn("loadVerbProgress: getUser failed:", e);
  }

  // 2) Kein User → reiner LocalStorage-Modus (alter Stand)
  if (!userId) {
    try {
      const key = getVerbProgressKey(languageId, profileId, weekIndex);
      const raw = window.localStorage.getItem(key);
      if (!raw) return {};
      const data = JSON.parse(raw);
      if (data && typeof data === "object") {
        return migrateVerbStates(data);
      }
      return {};
    } catch (e) {
      console.warn("loadVerbProgress (local): parse error", e);
      return {};
    }
  }

  // 3) Mit User → verb_progress ist Wahrheit
  try {
    const { data, error } = await supabase
      .from("verb_progress")
      .select(
        `
        verb_id,
        infinitive,
        translation,
        mode,
        table_attempts,
        table_good_runs,
        single_correct_total,
        box,
        correct_streak,
        forms,
        last_table_result,
        last_seen_at
      `
      )
      .eq("user_id", userId)
      .eq("profile_id", profileId || "default")
      .eq("language_id", languageId || "generic")
      .eq("week_index", weekIndex || 1);

    if (error) {
      console.warn("loadVerbProgress (remote): error", error);
      return {};
    }

    const result = {};
    for (const row of data || []) {
      const verbId = row.verb_id;
      if (!verbId) continue;

      result[verbId] = {
        id: verbId,
        infinitive: row.infinitive || "",
        translation: row.translation || "",
        mode: row.mode || "table",
        tableAttempts: row.table_attempts ?? 0,
        tableGoodRuns: row.table_good_runs ?? 0,
        singleCorrectTotal: row.single_correct_total ?? 0,
        box: row.box ?? 1,
        correctStreak: row.correct_streak ?? 0,
        lastTableResult: row.last_table_result || null,
        lastSeenAt: row.last_seen_at || null,
        forms: migrateFormKeys(row.forms) || null,
      };
    }

    // 4) Remote-Stand in localStorage spiegeln
    try {
      const key = getVerbProgressKey(languageId, profileId, weekIndex);
      window.localStorage.setItem(key, JSON.stringify(result));
    } catch (e) {
      console.warn("loadVerbProgress (remote→local): mirror failed", e);
    }

    return result;
  } catch (e) {
    console.warn("loadVerbProgress (remote): exception", e);
    return {};
  }
}

/**
 * Speichert den kompletten Verb-Progress für eine Woche.
 *
 * - Schreibt immer in localStorage (Offline-Support)
 * - Wenn User eingeloggt → upsert in Supabase (verb_progress)
 */
export async function saveVerbProgress(
  languageId,
  profileId,
  weekIndex,
  verbStates
) {
  // 1) Immer: lokalen Mirror aktualisieren
  if (typeof window !== "undefined") {
    try {
      const key = getVerbProgressKey(languageId, profileId, weekIndex);
      window.localStorage.setItem(key, JSON.stringify(verbStates || {}));
    } catch (e) {
      console.warn("saveVerbProgress (local): stringify error", e);
    }
  }

  // 2) User holen
  let userId = null;
  try {
    const { data, error } = await getSessionUser();
    if (!error && data?.user?.id) {
      userId = data.user.id;
    }
  } catch (e) {
    console.warn("saveVerbProgress: getUser failed:", e);
  }

  // Kein User → nur localStorage, fertig
  if (!userId) {
    return;
  }

  try {
    const states = verbStates || {};
    const nowIso = new Date().toISOString();

    const rows = Object.values(states).map((s) => ({
      user_id: userId,
      profile_id: profileId || "default",
      language_id: languageId || "generic",
      week_index: weekIndex || 1,
      verb_id: s.id,
      infinitive: s.infinitive || "",
      translation: s.translation || null,
      mode: s.mode || "table",
      table_attempts: s.tableAttempts ?? 0,
      table_good_runs: s.tableGoodRuns ?? 0,
      single_correct_total: s.singleCorrectTotal ?? 0,
      box: s.box ?? 1,
      correct_streak: s.correctStreak ?? 0,
      forms: s.forms || null,
      last_table_result: s.lastTableResult || null,
      last_seen_at: s.lastSeenAt || nowIso,
      updated_at: nowIso,
    }));

    if (!rows.length) {
      // Nichts zu speichern → ok
      return;
    }

    const { error } = await supabase
      .from("verb_progress")
      .upsert(rows, {
        onConflict:
          "user_id,profile_id,language_id,week_index,verb_id",
      });

    if (error) {
      console.warn("saveVerbProgress (remote): upsert error", error);
    }
  } catch (e) {
    console.warn("saveVerbProgress (remote): exception", e);
  }
}

/**
 * Löscht den Verb-Progress für eine bestimmte Woche:
 * - localStorage
 * - Supabase (falls eingeloggt)
 */
export async function resetVerbProgress(languageId, profileId, weekIndex) {
  // LocalStorage
  if (typeof window !== "undefined") {
    try {
      const key = getVerbProgressKey(languageId, profileId, weekIndex);
      window.localStorage.removeItem(key);
    } catch (e) {
      console.warn("resetVerbProgress (local): remove error", e);
    }
  }

  // User holen
  let userId = null;
  try {
    const { data, error } = await getSessionUser();
    if (!error && data?.user?.id) {
      userId = data.user.id;
    }
  } catch (e) {
    console.warn("resetVerbProgress: getUser failed:", e);
  }

  if (!userId) return;

  // Remote löschen
  try {
    const { error } = await supabase
      .from("verb_progress")
      .delete()
      .eq("user_id", userId)
      .eq("profile_id", profileId || "default")
      .eq("language_id", languageId || "generic")
      .eq("week_index", weekIndex || 1);

    if (error) {
      console.warn("resetVerbProgress (remote): delete error", error);
    }
  } catch (e) {
    console.warn("resetVerbProgress (remote): exception", e);
  }
}

/**
 * Stellt sicher, dass für alle Weekly-Set-Verben ein State existiert.
 *
 * @param {Array} weeklySet - Array der Weekly-Wörter (inkl. Verben)
 * @param {Object} existingStates - bereits gespeicherte States
 * @returns {{ verbs: Array, verbStates: Object }}
 *
 * - verbs: gefilterte Verben aus dem Weekly-Set
 * - verbStates: zusammengeführte States (existingStates + neue Defaults)
 */
export function ensureVerbStatesForWeeklySet(weeklySet, existingStates = {}) {
  const verbs = [];
  const verbStates = { ...existingStates };

  if (!Array.isArray(weeklySet)) {
    return { verbs, verbStates };
  }

  for (const w of weeklySet) {
    if (!w) continue;

    // nur type "verb" (falls gesetzt)
    if (w.type && w.type !== "verb") continue;

    const id = w.id || w.es;
    if (!id) continue;

    verbs.push(w);

    if (!verbStates[id]) {
      verbStates[id] = {
        id,
        infinitive: w.source || w.es || "",
        translation: w.target || w.de || "",
        mode: "table",
        tableAttempts: 0,
        tableGoodRuns: 0,
        lastTableResult: null,
        box: 1,
        singleCorrectTotal: 0,
        correctStreak: 0,
        lastSeenAt: null,
        forms: null,
      };
    } else {
      verbStates[id] = {
        ...verbStates[id],
        id: verbStates[id].id || id,
        infinitive: verbStates[id].infinitive || w.es || "",
        translation: verbStates[id].translation || w.de || "",
      };
    }
  }

  return { verbs, verbStates };
}
